import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientService from '../../../services/patientService';
import TreatmentPlanService from '../../../services/treatmentPlanService';
import PaymentService from '../../../services/paymentService';
import OrganizationService from '../../../services/organizationService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import './PatientBalance.css';

const PatientBalance = () => {
  const { patientId } = useParams();
  const auth = useAuth();

  const [patient, setPatient] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [activeTreatmentPlans, setActiveTreatmentPlans] = useState([]);
  const [patientPayments, setPatientPayments] = useState([]);
  const [totalActivePlans, setTotalActivePlans] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [balance, setBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(Number(amount || 0));
  };

  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return 'No especificado';
    const timezone = organization?.timezone || 'America/Mexico_City';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'No especificado';
    return date.toLocaleDateString('es-MX', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const statusLabels = {
    quoted: 'Presupuestado',
    accepted: 'Aceptado',
    in_progress: 'En curso',
    paused: 'Pausado',
    completed: 'Completado',
    rejected: 'Rechazado',
    active: 'Presupuestado'
  };

  const loadBalanceData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;
      if (!token || !patientId) return;

      const userId = auth.user?.profile?.sub || auth.user?.sub;
      const [patientData, plansData, paymentsData, orgData] = await Promise.all([
        PatientService.getPatientById(patientId, token),
        TreatmentPlanService.getTreatmentPlansByPatient(patientId, token),
        PaymentService.listByPatient(patientId, token, 100),
        userId ? OrganizationService.getCurrentUserOrganization(userId, token) : Promise.resolve(null)
      ]);

      setPatient(patientData);
      setOrganization(orgData);

      const plans = plansData?.data || [];
      const activeStatuses = ['accepted', 'in_progress', 'paused'];
      const activePlans = plans.filter((plan) => {
        const normalizedStatus = plan.status === 'active' ? 'quoted' : plan.status;
        return activeStatuses.includes(normalizedStatus);
      });

      const activePlansTotal = activePlans.reduce((sum, plan) => {
        return sum + (parseFloat(plan.totalCost) || 0);
      }, 0);

      const payments = paymentsData?.data || [];
      const sortedPayments = payments.sort((a, b) => new Date(b.paymentDate || b.createdAt) - new Date(a.paymentDate || a.createdAt));
      const netPaymentsTotal = sortedPayments.reduce((sum, payment) => {
        const amount = parseFloat(payment.amount) || 0;
        const refund = parseFloat(payment.refundAmount) || 0;
        return sum + (amount - refund);
      }, 0);

      setActiveTreatmentPlans(activePlans);
      setPatientPayments(sortedPayments);
      setTotalActivePlans(activePlansTotal);
      setTotalPayments(netPaymentsTotal);
      setBalance(netPaymentsTotal - activePlansTotal);
    } catch (error) {
      console.error('Error loading patient balance:', error);
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, auth.user?.profile?.sub, auth.user?.sub, patientId]);

  useEffect(() => {
    loadBalanceData();
  }, [loadBalanceData]);

  return (
    <div className="patient-balance-page">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : undefined}
        sectionTitle="Balance del Paciente"
      />

      <div className="balance-summary">
        <div className="balance-summary-item">
          <span>Balance</span>
          <strong className={balance > 0 ? 'balance-positive' : 'balance-negative'}>
            {formatCurrency(balance)}
          </strong>
        </div>
        <div className="balance-summary-item">
          <span>Total planes activos</span>
          <strong>{formatCurrency(totalActivePlans)}</strong>
        </div>
        <div className="balance-summary-item">
          <span>Total pagos</span>
          <strong>{formatCurrency(totalPayments)}</strong>
        </div>
      </div>

      {isLoading ? (
        <div className="balance-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="balance-grid">
          <div className="balance-card">
            <h3>Planes de tratamiento activos</h3>
            {activeTreatmentPlans.length === 0 ? (
              <div className="balance-empty">Sin planes activos</div>
            ) : (
              <ul className="balance-list">
                {activeTreatmentPlans.map((plan) => {
                  const normalizedStatus = plan.status === 'active' ? 'quoted' : plan.status;
                  return (
                    <li key={plan.id} className="balance-item">
                      <div className="balance-item-main">
                        <span className="balance-item-title">{plan.name || 'Plan sin nombre'}</span>
                        <span className="balance-item-amount">{formatCurrency(plan.totalCost)}</span>
                      </div>
                      <div className="balance-item-meta">
                        <span>{formatDateWithTimezone(plan.date || plan.createdAt)}</span>
                        <span>{statusLabels[normalizedStatus] || 'Sin estado'}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="balance-card">
            <h3>Pagos</h3>
            {patientPayments.length === 0 ? (
              <div className="balance-empty">Sin pagos registrados</div>
            ) : (
              <ul className="balance-list">
                {patientPayments.map((payment) => {
                  const amount = Number(payment.amount || 0);
                  const refund = Number(payment.refundAmount || 0);
                  const netAmount = amount - refund;
                  return (
                    <li key={payment.id} className="balance-item">
                      <div className="balance-item-main">
                        <span className="balance-item-title">
                          Pago {payment.paymentMethod || 'registrado'}
                        </span>
                        <span className="balance-item-amount">{formatCurrency(netAmount)}</span>
                      </div>
                      <div className="balance-item-meta">
                        <span>{formatDateWithTimezone(payment.paymentDate || payment.createdAt)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientBalance;
